import { Component, HostListener, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
  AbstractComponent,
  AuthenticationService,
  AuthorizationService,
  NavigationService,
  SolErrorPageType,
  SolLoader,
} from '@sol/components';
import { LabErrorPageComponent } from './components/lab-error-page/lab-error-page.component';
import { LabLoaderComponent } from './components/lab-loader/lab-loader.component';
import { LabNavbarComponent } from './components/lab-navbar/lab-navbar.component';
import { LabOverlaysComponent } from './components/lab-overlays/lab-overlays.component';
import { LabPageComponent } from './components/lab-page/lab-page.component';
import { LabBannerHostComponent } from './playground/lab-banner-host/lab-banner-host.component';
import { LabCommandPaletteComponent } from './playground/lab-command-palette/lab-command-palette.component';
import { CommandPaletteService } from './services/command-palette.service';
import { AppState } from './app.state';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    LabOverlaysComponent,
    LabNavbarComponent,
    LabBannerHostComponent,
    LabCommandPaletteComponent,
    LabPageComponent,
    LabLoaderComponent,
    LabErrorPageComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent extends AbstractComponent<AppState> implements OnInit {
  protected readonly SolErrorPageType = SolErrorPageType;
  protected readonly SolLoader = SolLoader;

  private readonly authenticationService = inject(AuthenticationService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly navigationService = inject(NavigationService);
  private readonly commandPaletteService = inject(CommandPaletteService);

  @HostListener('window:keydown', ['$event'])
  protected onWindowKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (this.commandPaletteService.open()) {
        this.commandPaletteService.closePalette();
      } else {
        this.commandPaletteService.openPalette();
      }
    }
  }

  protected readonly showNavbar$ = this.compute(state => state.showNavbar);
  protected readonly activeRouteStatus$ = this.compute(state => state.activeRouteStatus);

  constructor() {
    super(AppState.of());

    this.navigationService.watchComputed(
      nss => nss.activeRouteStatus,
      status => this.update(state => state.withActiveRouteStatus(status)),
    );
    this.navigationService.watch(nss => {
      const route = nss.activeRoute;
      this.update(state => state.withShowNavbar(route?.showNavbar ?? true));
    });
  }

  ngOnInit(): void {
    this.authenticationService.fetch();
    this.authorizationService.setAuthorities([], true);

    // Keyboard-only focus ring. Tab sets `data-keyboard-nav` on <body>, any
    // pointer event clears it. SCSS gates the outline on that flag, so the
    // ring shows up when someone tabs through but stays off when they click
    // a field (the browser's built-in :focus-visible would otherwise light
    // up text inputs on click and leave a stray outline behind).
    window.addEventListener('keydown', e => {
      if (e.key === 'Tab') document.body.setAttribute('data-keyboard-nav', 'true');
    });
    window.addEventListener('pointerdown', () => {
      document.body.removeAttribute('data-keyboard-nav');
    });
  }
}
